import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp, CURRENCIES } from '../context/AppContext';
import { Menu, X, User as UserIcon, LogOut, Package, CreditCard, ChevronDown, Check, ShoppingCart, DollarSign, Search, Globe } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';

export const Navbar = () => {
  const { user, logout, mySims, currentCurrency, setCurrentCurrency } = useApp();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');
  const [isMobileCurrencyOpen, setIsMobileCurrencyOpen] = useState(false);
  const [tempCurrency, setTempCurrency] = useState(currentCurrency);

  // States that are considered expired/unusable
  const EXPIRED_STATES = ['EXPIRED', 'REVOKED', 'DELETED'];
  // Filter active sims - any sim not in expired states is considered active
  const activeSims = mySims.filter(s => !EXPIRED_STATES.includes(s.roamifyStatus || s.status));
  const hasUnlimited = activeSims.some(s => s.dataTotal >= 999);
  const totalDataLeft = activeSims.reduce((acc, sim) => acc + ((sim.dataTotal || 0) - (sim.dataUsed || 0)), 0);
  
  // Determine eSIM status for display
  const hasActiveSims = activeSims.length > 0;
  const hasDataLeft = totalDataLeft > 0 || hasUnlimited;
  
  // Get status display info
  const getStatusDisplay = () => {
    if (!hasActiveSims) {
      return { text: 'No active eSIM', isWarning: true };
    }
    if (hasUnlimited) {
      return { text: '∞ GB Left', isWarning: false };
    }
    if (totalDataLeft <= 0) {
      return { text: '0GB Left', isWarning: true };
    }
    return { text: `${totalDataLeft.toFixed(2)} GB Left`, isWarning: false };
  };
  
  const statusDisplay = getStatusDisplay();

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsProfileOpen(false);
  };

  const filteredCurrencies = CURRENCIES.filter(c => 
    c.name.toLowerCase().includes(currencySearch.toLowerCase()) || 
    c.code.toLowerCase().includes(currencySearch.toLowerCase())
  );

  return (
    <>
      <nav className="fixed top-4 left-0 right-0 z-50 px-3 sm:px-6 lg:px-8 pointer-events-none" style={{ maxWidth: '100vw' }}>
        <div className="max-w-7xl mx-auto bg-white/[0.55] dark:bg-neutral-900/70 backdrop-blur-xl rounded-full border-2 border-black dark:border-neutral-200 shadow-sm pointer-events-auto h-20 px-4 sm:px-6 flex justify-between items-center transition-all duration-300 relative z-50">
          <div className="flex items-center cursor-pointer flex-shrink-0 w-1/4" onClick={() => navigate('/')}>
            <div className="flex-shrink-0 flex items-center gap-3">
              <div className="flex items-center justify-center text-black dark:text-white">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6.5" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <span className="font-extrabold text-xl tracking-tight text-gray-900 dark:text-neutral-100" style={{ fontFamily: "'Montserrat', sans-serif" }}>Connectify</span>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-end space-x-6 flex-shrink-0 w-3/4 relative">
            <Link to="/destinations" className="text-sm font-bold text-gray-700 dark:text-neutral-300 hover:text-black dark:hover:text-white transition-colors mr-2" data-testid="nav-destinations">
              All Destinations
            </Link>

            <div className="relative">
              <button 
                onClick={() => { setIsCurrencyOpen(!isCurrencyOpen); setCurrencySearch(''); }}
                className="text-gray-800 dark:text-neutral-200 hover:text-black dark:hover:text-white font-medium text-xs flex items-center gap-1 focus:outline-none uppercase"
                data-testid="currency-selector"
              >
                {currentCurrency.symbol} - {currentCurrency.code} <ChevronDown size={12} className={`transition-transform duration-200 ${isCurrencyOpen ? 'rotate-180' : ''}`} />
              </button>

              {isCurrencyOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsCurrencyOpen(false)}></div>
                  <div className="absolute right-0 top-full mt-4 w-80 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl rounded-[2rem] shadow-2xl border border-gray-200 dark:border-neutral-800 py-0 z-50 animate-fade-in-down overflow-hidden origin-top-right ring-1 ring-black/5 dark:ring-white/5">
                    <div className="p-5 border-b border-gray-100 dark:border-neutral-800 bg-white/50 dark:bg-neutral-900/50">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-gray-900 dark:text-neutral-100 text-sm">Select Currency</h3>
                        <button onClick={() => setIsCurrencyOpen(false)} className="text-gray-400 dark:text-neutral-500 hover:text-black dark:hover:text-white transition-colors bg-white dark:bg-neutral-900 hover:bg-gray-100 dark:hover:bg-neutral-800 p-1.5 rounded-full shadow-sm border border-gray-100 dark:border-neutral-800"><X size={16} /></button>
                      </div>
                      <div className="relative group">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500 group-focus-within:text-black dark:group-focus-within:text-white transition-colors" />
                        <input 
                          type="text" 
                          placeholder="Search..." 
                          value={currencySearch}
                          onChange={(e) => setCurrencySearch(e.target.value)}
                          autoFocus
                          className="w-full bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-xl py-3 pl-9 pr-3 text-xs font-bold text-gray-900 dark:text-neutral-100 focus:outline-none focus:border-black dark:focus:border-white focus:ring-0 transition-all placeholder:text-gray-400 dark:placeholder:text-neutral-500"
                        />
                      </div>
                    </div>
                    <div className="max-h-[320px] overflow-y-auto no-scrollbar bg-white dark:bg-neutral-900">
                      {filteredCurrencies.length > 0 ? (
                        filteredCurrencies.map((curr) => {
                          const isSelected = currentCurrency.code === curr.code;
                          return (
                          <button 
                            key={curr.code}
                            onClick={() => { setCurrentCurrency(curr); setIsCurrencyOpen(false); setCurrencySearch(''); }}
                            className={`w-full text-left px-5 py-3.5 flex justify-between items-center transition-all border-b border-gray-50 dark:border-neutral-800 last:border-0 hover:bg-gray-50 dark:hover:bg-neutral-800 group ${isSelected ? 'bg-gray-50 dark:bg-white' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border ${isSelected ? 'bg-black text-white border-black dark:bg-black dark:border-black' : 'bg-gray-100 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border-gray-200 dark:border-neutral-700 group-hover:border-gray-300 dark:group-hover:border-neutral-600'}`}>
                                {curr.symbol}
                              </span>
                              <div className="flex flex-col">
                                <span className={`text-xs font-bold ${isSelected ? 'text-black dark:text-black' : 'text-gray-700 dark:text-neutral-200'}`}>{curr.code}</span>
                                <span className={`text-[10px] font-medium ${isSelected ? 'text-gray-500 dark:text-gray-500' : 'text-gray-400 dark:text-neutral-500'}`}>{curr.name}</span>
                              </div>
                            </div>
                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>}
                          </button>
                          );
                        })
                      ) : (
                        <div className="p-8 text-center flex flex-col items-center">
                          <div className="w-8 h-8 bg-gray-50 dark:bg-neutral-950 rounded-full flex items-center justify-center mb-2"><Search size={14} className="text-gray-300 dark:text-neutral-600"/></div>
                          <p className="text-gray-400 dark:text-neutral-500 text-xs font-medium">No currency found</p>
                        </div>
                      )}
                    </div>
                    <div className="px-5 py-3 bg-gray-50 dark:bg-neutral-950 border-t border-gray-100 dark:border-neutral-800 flex justify-center">
                      <p className="text-[9px] text-gray-400 dark:text-neutral-500 font-bold uppercase tracking-widest flex items-center gap-1.5">
                        <Globe size={10} /> Live Exchange Rates
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {!user && <Link to="/checkout" className="text-gray-800 dark:text-neutral-200 hover:text-black dark:hover:text-white px-1" data-testid="nav-cart"><ShoppingCart size={20} /></Link>}

            <ThemeToggle className="!w-8 !h-8" />

            {user ? (
              <div className="flex items-center gap-4">
                <Link 
                  to="/dashboard" 
                  className={`text-black dark:text-white font-semibold flex items-center gap-2 px-3 py-1 rounded-full border transition-colors text-xs ${
                    statusDisplay.isWarning 
                      ? 'bg-red-50 border-red-200 hover:bg-red-100' 
                      : 'bg-white/60 border-gray-200/50 hover:bg-white'
                  }`} 
                  data-testid="nav-data-left"
                >
                  <div className={`w-2 h-2 rounded-full ${statusDisplay.isWarning ? 'bg-red-500' : 'bg-green-500 animate-pulse'}`}></div>
                  {statusDisplay.text}
                </Link>
                <div className="relative">
                  <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center gap-3 focus:outline-none" data-testid="profile-button">
                    <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center border-2 border-white/50 shadow-sm"><UserIcon size={16} /></div>
                  </button>
                  {isProfileOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)}></div>
                      <div className="absolute right-0 top-full mt-4 w-72 bg-white/30 backdrop-blur-md rounded-[2rem] shadow-2xl border-2 border-black dark:border-neutral-300 py-0 animate-fade-in-down z-50 overflow-hidden origin-top-right">
                        <div className="px-6 py-5 border-b border-black/10 bg-white/20">
                          <p className="text-xs font-bold text-gray-500 dark:text-neutral-500 uppercase tracking-wider mb-1">Signed in as</p>
                          <p className="text-sm font-bold text-black dark:text-white truncate">{user.email}</p>
                        </div>
                        <div className="py-2 bg-white/10">
                          <Link to="/dashboard" onClick={() => setIsProfileOpen(false)} className="flex items-center px-6 py-3 text-sm font-bold text-gray-800 dark:text-neutral-200 hover:text-black dark:hover:text-white hover:bg-black/5 transition-colors"><Package className="mr-3 h-5 w-5" /> My eSIM Plans</Link>
                          <Link to="/dashboard" onClick={() => setIsProfileOpen(false)} className="flex items-center px-6 py-3 text-sm font-bold text-gray-800 dark:text-neutral-200 hover:text-black dark:hover:text-white hover:bg-black/5 transition-colors"><CreditCard className="mr-3 h-5 w-5" /> Orders</Link>
                        </div>
                        <div className="border-t border-black/10 bg-white/10">
                          <button onClick={handleLogout} className="flex w-full items-center px-6 py-4 text-sm font-bold text-red-600 dark:text-red-400 hover:bg-red-50/50 transition-colors" data-testid="logout-button"><LogOut className="mr-3 h-5 w-5" /> Logout</button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-signin"><button className="bg-transparent border border-gray-900 text-gray-900 dark:text-neutral-100 px-5 py-2 rounded-full text-xs font-bold hover:bg-black hover:text-white transition-all">SIGN IN</button></Link>
            )}
          </div>

          <div className="flex items-center md:hidden gap-4">
            <button onClick={() => { setIsMenuOpen(!isMenuOpen); setIsMobileCurrencyOpen(false); }} className="inline-flex items-center justify-center p-2 rounded-md text-gray-800 dark:text-neutral-200 hover:text-black dark:hover:text-white focus:outline-none" data-testid="mobile-menu-toggle">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="pointer-events-auto absolute top-24 left-4 right-4 md:hidden z-50">
            <div className="max-w-7xl mx-auto">
              <div className={`bg-white/80 dark:bg-black/70 backdrop-blur-md rounded-[2rem] shadow-xl border border-gray-400 dark:border-white/30 overflow-hidden transition-all duration-300 animate-fade-in-down ${isMobileCurrencyOpen ? 'h-[500px] flex flex-col' : 'p-6'}`}>
                {isMobileCurrencyOpen ? (
                  <>
                    <div className="flex justify-between items-center p-6 border-b border-gray-400/30 dark:border-white/20 bg-white/20 dark:bg-white/5">
                      <h3 className="text-xl font-bold text-black dark:text-white">Select Currency</h3>
                      <button onClick={() => setIsMobileCurrencyOpen(false)} className="text-gray-600 dark:text-neutral-300"><X size={24} /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
                      {CURRENCIES.map((curr) => (
                        <button key={curr.code} onClick={() => setTempCurrency(curr)} className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${tempCurrency.code === curr.code ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' : 'bg-white/50 dark:bg-white/10 border-transparent text-gray-800 dark:text-white'}`}>
                          <span className="font-bold text-sm">{curr.name}</span>
                          <span className="font-mono">{curr.symbol} {curr.code}</span>
                        </button>
                      ))}
                    </div>
                    <div className="p-6 border-t border-gray-400/30 dark:border-white/20 bg-white/20 dark:bg-white/5">
                      <button onClick={() => { setCurrentCurrency(tempCurrency); setIsMobileCurrencyOpen(false); }} className="w-full bg-black text-white dark:bg-white dark:text-black font-bold text-lg py-4 rounded-xl">Confirm</button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    {user && (
                      <>
                        <div className="px-1 mb-2">
                          <p className="text-xs font-bold text-gray-500 dark:text-neutral-400 uppercase">Welcome back,</p>
                          <p className="text-xl font-black text-black dark:text-white">{user.name}</p>
                        </div>
                        <div className={`px-3 py-3 text-sm font-semibold flex items-center gap-2 rounded-xl border ${
                          statusDisplay.isWarning 
                            ? 'text-red-800 dark:text-red-300 bg-red-50/50 dark:bg-red-500/10 border-red-100 dark:border-red-500/30' 
                            : 'text-green-800 dark:text-green-300 bg-green-50/50 dark:bg-emerald-500/10 border-green-100 dark:border-emerald-500/30'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${statusDisplay.isWarning ? 'bg-red-500' : 'bg-green-500'}`}></div>
                          {statusDisplay.text.replace(' Left', ' Data Remaining').replace('No active eSIM', 'No active eSIM')}
                        </div>
                      </>
                    )}
                    <div className="flex items-center justify-between px-2">
                      <Link to="/destinations" onClick={() => setIsMenuOpen(false)} className="text-lg font-bold text-gray-900 dark:text-white">All Destinations</Link>
                      <div className="flex items-center gap-3">
                        <button onClick={() => { setTempCurrency(currentCurrency); setIsMobileCurrencyOpen(true); }} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/50 dark:bg-white/10 border border-gray-200 dark:border-white/30 text-gray-900 dark:text-white"><DollarSign size={20} /></button>
                        {!user && <Link to="/checkout" onClick={() => setIsMenuOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/50 dark:bg-white/10 border border-gray-200 dark:border-white/30 text-gray-900 dark:text-white"><ShoppingCart size={20} /></Link>}
                      </div>
                    </div>
                    <div className="h-px bg-gray-400/30 dark:bg-white/20 w-full my-2"></div>
                    {user ? (
                      <div className="space-y-2">
                        <Link to="/dashboard" onClick={() => setIsMenuOpen(false)} className="block px-2 py-2 text-base font-semibold text-gray-800 dark:text-white">My SIM Plans</Link>
                        <button onClick={handleLogout} className="w-full text-left block px-2 py-2 text-base font-semibold text-red-600 dark:text-red-400">Logout</button>
                      </div>
                    ) : (
                      <Link to="/login" onClick={() => setIsMenuOpen(false)} className="block w-full text-center bg-black text-white dark:bg-white dark:text-black px-4 py-4 rounded-xl font-bold">Sign In</Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .animate-fade-in-down { animation: fadeInDown 0.2s ease-out forwards; }
        @keyframes fadeInDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </>
  );
};
